import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle2, Crown, Award, Star } from 'lucide-react';
import { toast } from 'sonner';

const TIER_META = {
  LEVEL1: { tagline: 'Essential maintenance & reinforcement', icon: Star, accent: '#1B3A87', popular: false },
  LEVEL2: { tagline: 'Comprehensive care & advanced training', icon: Award, accent: '#C8102E', popular: true },
  LEVEL3: { tagline: 'Ultimate training & exclusive benefits', icon: Crown, accent: '#B08D57', popular: false },
};

export default function Membership() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get('/memberships/tiers').then(r => setTiers(r.data.tiers));
  }, []);

  const subscribe = async (tier) => {
    setLoading(tier.id);
    try {
      const { data } = await api.post('/memberships/checkout', { plan: tier.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(null); }
  };

  return (
    <div className='p-6 lg:p-8 max-w-6xl mx-auto'>
      <div className='mb-8 text-center'>
        <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Memberships</div>
        <h1 className='font-display text-4xl font-bold text-[#0B1220] mt-2'>The Ultimate Dog Membership</h1>
        <p className='text-slate-600 mt-3 max-w-2xl mx-auto'>
          Tailored for dogs who've completed Board & Train. Continue with structured care and ongoing training.
        </p>
        {user?.membership_level && (
          <Badge className='mt-4 bg-[#167A5A] text-white border-0'>You're currently on {user.membership_level}</Badge>
        )}
      </div>

      <div className='grid md:grid-cols-3 gap-5' data-testid='membership-tiers'>
        {tiers.map((t) => {
          const meta = TIER_META[t.id] || TIER_META.LEVEL1;
          const Icon = meta.icon;
          return (
            <Card key={t.id} className={`relative p-6 border ${meta.popular ? 'border-[#C8102E] shadow-xl scale-[1.02]' : 'border-slate-200'}`} data-testid={`tier-card-${t.id}`}>
              {meta.popular && (
                <Badge className='absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8102E] text-white border-0 px-3 py-1 font-bold uppercase tracking-wider text-[10px]'>Most Popular</Badge>
              )}
              <div className='flex items-center gap-2'>
                <div className='w-10 h-10 rounded-lg flex items-center justify-center text-white' style={{ background: meta.accent }}>
                  <Icon className='w-5 h-5' />
                </div>
                <div>
                  <div className='font-display font-bold text-xl text-[#0B1220]'>{t.label.split('—')[0]}</div>
                  <div className='text-xs text-slate-500'>{meta.tagline}</div>
                </div>
              </div>
              <div className='mt-5'>
                <div className='text-xs text-slate-500 mb-1'>starting at</div>
                <div className='flex items-baseline gap-1'>
                  <span className='font-display font-bold text-5xl tabular-nums text-[#0B1220]'>${t.price.toFixed(0)}</span>
                  <span className='text-slate-500'>/month</span>
                </div>
              </div>
              <ul className='mt-5 space-y-2.5'>
                {t.perks.map((p) => (
                  <li key={p} className='flex items-start gap-2 text-sm text-slate-700'>
                    <CheckCircle2 className='w-4 h-4 text-[#167A5A] mt-0.5 shrink-0' /> {p}
                  </li>
                ))}
              </ul>
              <Button className='w-full mt-6 h-11' style={{ background: meta.accent }} onClick={() => subscribe(t)} disabled={loading === t.id} data-testid={`tier-subscribe-${t.id}`}>
                {loading === t.id ? 'Redirecting…' : `Get Started — $${t.price.toFixed(0)}/mo`}
              </Button>
            </Card>
          );
        })}
      </div>

      <Card className='p-5 mt-6 border border-slate-200 bg-slate-50'>
        <p className='text-sm text-slate-600 text-center'>
          All memberships are billed monthly via Stripe. <strong>Cancel anytime</strong> from your dashboard. True recurring Stripe Subscription with auto-renewal is coming in Phase 3.
        </p>
      </Card>
    </div>
  );
}
